/**
 * Projective transform from the domain's unit square to screen pixels.
 *
 * The overlay is a separate WebGL2 canvas rather than a MapLibre custom layer,
 * so it needs its own mapping from grid space to the screen. The domain is a
 * planar quad, so a homography reproduces MapLibre's projection exactly —
 * including pitch — from just the four projected corners.
 */

export type Mat3 = Float32Array;

/**
 * Solve the 8 unknowns of the homography taking the unit square corners
 * (0,0), (1,0), (1,1), (0,1) to `dst` in the same order.
 *
 * Returns a column-major mat3 ready for `uniformMatrix3fv`.
 */
export function unitSquareToQuad(dst: [number, number][]): Mat3 {
  const src: [number, number][] = [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ];

  // 8x8 system: [x y 1 0 0 0 -x*X -y*X] · h = X, and likewise for Y.
  const A: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i];
    const [X, Y] = dst[i];
    A.push([x, y, 1, 0, 0, 0, -x * X, -y * X]);
    b.push(X);
    A.push([0, 0, 0, x, y, 1, -x * Y, -y * Y]);
    b.push(Y);
  }

  const h = solve8(A, b);
  if (!h) return identity();

  // Column-major for GLSL.
  return new Float32Array([h[0], h[3], h[6], h[1], h[4], h[7], h[2], h[5], 1]);
}

export function identity(): Mat3 {
  return new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);
}

/** Gaussian elimination with partial pivoting. */
function solve8(A: number[][], b: number[]): number[] | null {
  const n = 8;
  const M = A.map((row, i) => [...row, b[i]]);

  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (Math.abs(M[pivot][col]) < 1e-12) return null;
    [M[col], M[pivot]] = [M[pivot], M[col]];

    const d = M[col][col];
    for (let c = col; c <= n; c++) M[col][c] /= d;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col];
      if (f === 0) continue;
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row) => row[n]);
}

/** Apply a column-major mat3 homography to a point, with the perspective divide. */
export function applyHomography(h: Mat3, x: number, y: number): [number, number] {
  const X = h[0] * x + h[3] * y + h[6];
  const Y = h[1] * x + h[4] * y + h[7];
  const W = h[2] * x + h[5] * y + h[8];
  return [X / W, Y / W];
}
